import React, { useState, useCallback } from 'react';
import { Icons } from '../Icons';

/**
 * SearchBox — A styled search input with icon and focus state tracking.
 *
 * The entire container is clickable — clicking anywhere focuses the input.
 * Placeholder text hides on focus and reappears on blur for a clean look.
 *
 * @param {func}   handleOnChange  - Fires on every keystroke
 * @param {func}   handleOnKeyDown - Fires on key press (e.g. Enter to submit)
 * @param {string} value           - Controlled input value
 * @param {string} placeholder     - Placeholder text (hidden when focused)
 * @param {string} size            - 'Default' | 'Large'
 */
export const SearchBox = ({
    handleOnChange,
    handleOnKeyDown,
    value,
    placeholder = "Search",
    customStyle = {},
    size = "Default"
}) => {
    const [isFocused, setIsFocused] = useState(false);
    const inputRef = React.useRef(null);

    const sizeClass = size === "Large" ? "search-box--large" : "search-box--default";
    const focusClass = isFocused ? "is-focused" : "";
    const valueClass = value && value.length > 0 ? "has-value" : "";

    /** Clicking the container (not the input itself) focuses the input */
    const handleContainerClick = useCallback((e) => {
        if (e.target !== inputRef.current) {
            inputRef.current?.focus();
        }
    }, []);

    return (
        <div
            className={`search-box-container ${sizeClass} ${focusClass} ${valueClass}`}
            style={customStyle}
            onClick={handleContainerClick}
            role="search"
        >
            <div className="search-box-icon">
                <Icons name="search" size={16} />
            </div>
            <input
                ref={inputRef}
                type="text"
                className="search-box-input body-s-semibold"
                value={value}
                placeholder={isFocused ? "" : placeholder}
                onChange={handleOnChange}
                onKeyDown={handleOnKeyDown}
                onFocus={() => setIsFocused(true)}
                onBlur={() => setIsFocused(false)}
                autoComplete="off"
                aria-label={placeholder}
            />
        </div>
    );
};